import { useRef } from 'react';
import { Table as TRTable } from '@tanstack/react-table';
import { UploadButton, DownloadButton } from '@anytag/shared-atoms';
import { ListWithPagination, UploadCsvModal } from '@anytag/shared-molecules';
import { useDownloadFile, useToggleState, useParamsSearch } from '@anytag/shared-hooks';
import { InstagramStoryPost, useAllInstagramStoryPostsQuery } from '@anytag/graphql';
import {
  getOffset,
  getUploadApiUrl,
  getValuableVariables,
  convertOrderToORDER,
  getAccountManagementApiUrl
} from '@anytag/shared-utils';
import { ProfilePostsCount, ProfilePostsFilter, AnalyticsInstagramStoryPostsList } from '@anytag/shared-organisms';
import { useInitialAnalyticsPosts } from '../hooks';
import { AnalyticsIgStoryPostsListSearchSchemaType } from '../schema-types';

export const AnalyticsInstagramStoryPosts = () => {
  const uploadModal = useToggleState();
  const tableRef = useRef<TRTable<InstagramStoryPost>>(null);
  const { downloading, handleDownloadFile } = useDownloadFile();
  const { brandAccountId, mainSocialAccountId } = useInitialAnalyticsPosts({
    socialMedia: 'instagram',
    document: (name) => ({
      appHeader: name ? 'appHeader.StoryPostListWithAccountName' : 'appHeader.Story Post List',
      title: name ? 'documentTitle.StoryPostListWithAccountName' : 'documentTitle.Story Post List'
    })
  });
  const { page, limit, sort, filter, setFilter } = useParamsSearch<AnalyticsIgStoryPostsListSearchSchemaType>();
  const { data, previousData, loading, refetch } = useAllInstagramStoryPostsQuery({
    skip: !brandAccountId || !mainSocialAccountId,
    variables: getValuableVariables({
      limit,
      offset: getOffset(page, limit),
      accountId: Number(mainSocialAccountId),
      orderBy: { field: sort.field, order: convertOrderToORDER(sort.order) },
      ...filter
    })
  });

  const result = loading ? previousData : data;
  const totalRecords = result?.allInstagramStoryPostsCount?.totalNumber;

  return (
    <>
      <ListWithPagination pagination={{ totalRecords }}>
        <ProfilePostsFilter filter={filter} setFilter={setFilter} hasPeriod />

        <ProfilePostsCount count={totalRecords}>
          <UploadButton onClick={uploadModal.open} css={{ marginRight: '8px' }} />
          <DownloadButton
            loading={downloading}
            onClick={() =>
              handleDownloadFile({
                params: getValuableVariables({
                  endDate: filter.endDate,
                  startDate: filter.startDate,
                  selectedColumns: tableRef.current
                    ?.getVisibleFlatColumns()
                    .reduce<
                      string[]
                    >((acc, curr) => (curr.columnDef.meta?.sortField && curr.id ? [...acc, curr.id] : acc), [])
                }),
                filenamePrefix: 'storyPosts',
                url: getUploadApiUrl(`analytics/story_posts/${mainSocialAccountId}/csv/custom`)
              })
            }
          />
        </ProfilePostsCount>

        <AnalyticsInstagramStoryPostsList
          loading={loading}
          tableRef={tableRef}
          mainSocialAccountId={mainSocialAccountId}
          data={result?.allInstagramStoryPosts || []}
        />
      </ListWithPagination>

      <UploadCsvModal
        onFileUploaded={refetch}
        open={uploadModal.status}
        onClose={uploadModal.close}
        uploadFile={{
          fileNamePrefix: 'story_posts.csv',
          url: getAccountManagementApiUrl(`api/analytics/story_posts/${mainSocialAccountId}/csv`)
        }}
        downloadFile={{
          extension: '.csv',
          filenamePrefix: 'story_posts',
          params: { startDate: filter.startDate, endDate: filter.endDate },
          url: getUploadApiUrl(`analytics/story_posts/${mainSocialAccountId}/csv`)
        }}
      />
    </>
  );
};
